import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { bookingCatalogApi } from '../api/bookingCatalogApi';
import { CreateBookingServiceDto, UpdateBookingServiceDto } from '../types/booking';

const QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
  staleTime: 60 * 1000,
} as const;

const QUERY_KEYS = {
  services: ['booking-services'],
};

export const useServices = () =>
  useQuery({
    queryKey: QUERY_KEYS.services,
    queryFn: () => bookingCatalogApi.getServices().then((res) => res.data),
    ...QUERY_OPTIONS,
  });

export const useCreateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBookingServiceDto) => bookingCatalogApi.createService(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services });
      toast.success('Service created successfully');
    },
    onError: () => toast.error('Failed to create service'),
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingServiceDto }) => bookingCatalogApi.updateService(id, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services });
      toast.success('Service updated successfully');
    },
    onError: () => toast.error('Failed to update service'),
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookingCatalogApi.deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services });
      toast.success('Service deleted successfully');
    },
    onError: () => toast.error('Failed to delete service'),
  });
};

// No dedicated deactivate endpoint exists; deactivation is a full update with active=false.
export const useDeactivateService = () => useUpdateService();


